<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <modal-dialog
            ref="reopenLeadModal"
            label="Lead heropenen"
            description="Lead heropenen"
            :size="4"
        >
            <flex-container>
                <flex-container direction="column" :gap="Size.sm">
                    <div>
                        <text-heading>
                            <span>Lead heropenen</span>
                        </text-heading>
                    </div>

                    <text-description>
                        Het is hieronder mogelijk om deze lead opnieuw te openen.
                    </text-description>
                </flex-container>
                <flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.phase"
                            :schema="formSchema.phase"
                        >
                            <template #label>
                                <input-label>
                                    Fase
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    :items="availablePhases"
                                    label-key-name="name"
                                />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="formData.assignedToUser"
                            :schema="formSchema.assignedToUser"
                        >
                            <template #label>
                                <input-label>
                                    Toewijzen aan gebruiker (optioneel)
                                </input-label>
                            </template>
                            <template #input>
                                <user-select-input
                                    :users="availableUsers"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
            </flex-container>

            <template #footer>
                <dialog-footer>
                    <button-outline @click="redirectToDetail">
                        Annuleren
                    </button-outline>

                    <button-block-confirm :disabled="!validationBindings.isValid.value" @confirmed="onSave($event)">
                        Heropenen

                        <template #confirmation>
                            Weet je het zeker?
                        </template>
                    </button-block-confirm>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
</template>

<script setup lang="ts">
    import {
        ButtonBlockConfirm,
        ButtonOutline,
        DialogFooter,
        type ExposedOverlay,
        FlexContainer,
        FormValidation,
        InputGroup,
        InputLabel,
        ModalDialog,
        SelectInput,
        TextDescription,
        TextHeading,
        Size,
    } from '@keyvue/one-ui';

    import { Lead, Phase, PhaseId, User, UserId } from '@keyvue/one-api-client';

    import { createResourceIdentifier, ReferralAPI, ReferralStatus } from '@keyvue/one-api-client-legacy';

    import { computed, ref, unref } from 'vue';
    import { object } from 'yup';
    import { useRouter } from 'vue-router';
    import useNotify from '@composables/useNotify';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import useClient from '@composables/useClient';
    import UserSelectInput from '@components/Inputs/UserSelectInput.vue';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        contactId: string;
        leadId: string;
    }

    interface FormData {
        phase: Phase | null;
        assignedToUser: User | null;
    }

    const { client, currentUser } = await useClient();
    if (!currentUser.value) {
        throw new Error('Not authenticated');
    }

    const { notifySaving, notifySaved, notifyError } = useNotify();
    const props = defineProps<Props>();
    const reopenLeadModal = ref<ExposedOverlay>();
    const lead = await client.get(Lead, props.leadId);
    const router = useRouter();
    const eventBus = useEventBus();

    const formData = ref<FormData>({
        phase: null,
        assignedToUser: null,
    });

    const formSchema = {
        phase: object({ id: PhaseId.validationSchema }),
        assignedToUser: object({ id: UserId.validationSchema }).nullable(),
    };

    const reopenLegacyReferral = async () => {
        const closeNotification = notifySaving('Lead wordt heropend', 0);
        try {
            const referral = await ReferralAPI.get(lead.id.value);
            if (!referral) {
                throw new Error('Legacy referral kon niet worden opgehaald');
            }

            referral.status = ReferralStatus.OPEN;
            referral.statusReason = null;
            referral.newStatusReason = null;
            referral.phase = formData.value.phase ? createResourceIdentifier('pipeline_phases', formData.value.phase.id.value) : null;
            referral.assignedTo = formData.value.assignedToUser ? createResourceIdentifier('users', formData.value.assignedToUser.id.value) : referral.assignedTo;

            await ReferralAPI.update(lead.id.value, referral);
            eventBus.dispatch('resource-manipulated', 'Lead');
            notifySaved('Lead is heropend');
        } catch (error) {
            notifyError(error);
        } finally {
            if (closeNotification) {
                closeNotification();
            }
        }
    };

    const [
        assignableResponsibleUserCollection,
    ] = await Promise.all([
        await client.getSubResources(lead.assignee.team, User, 'available-users'),
    ]);

    const availablePhases = computed(() => lead.pipeline.activePhases);
    const availableUsers = computed(() => unref<User[]>(assignableResponsibleUserCollection.data));

    const redirectToDetail = () => {
        router.push({
            name: 'contactLeadDetail',
            params: {
                contactId: props.contactId,
                leadId: props.leadId,
            },
        });
    };

    const onSave = async (event: any) => {
        await reopenLegacyReferral();
        event.button.setState('completed');
        redirectToDetail();
    };

    useOverlayAsRoute({
        overlay: reopenLeadModal,
        isSideSheet: false,
        redirectMethod: redirectToDetail,
    });
</script>
